import {
  Card,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isCurrency,
} from 'platform/components';
import {Box, HStack, Stack, Text} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';
import {mixed, object, SchemaOf, string} from 'yup';

import {useRef, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  type UpdateOfferAlternativeRequestBody,
  useUpdateOfferAlternativeMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {NoPermissionTooltip} from '@dms/shared';
import {$MoneyAmount} from '@dms/teas';

import {getAlternativeLabel} from '../utils/getAlternativeLabel';
import {type OfferAlternativeWithRights} from '../utils/getAlternativeRights';
import {handleOfferAlternativeError} from '../utils/handleOfferAlternativeError';

type DiscountFormValues = {
  discountPrice: string | null;
  discountPercentage: string | null;
  discountReason: string | null;
};

interface AlternativeDiscountFormProps {
  businessCaseId: string;
  alternative: OfferAlternativeWithRights;
}

/**
 * Customer discount of one alternative. Same layout and percentage ⇄ amount sync as the deal
 * vehicle's discount form; saving creates a new revision of the alternative.
 */
export function AlternativeDiscountForm(props: AlternativeDiscountFormProps) {
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const formApiRef = useRef<UseFormReturn<DiscountFormValues> | null>(null);
  const [lastTouched, setLastTouched] = useState<'percentage' | 'amount'>();
  const [patchAlternative, {isLoading}] = useUpdateOfferAlternativeMutation();
  const {terms, canEditDiscount} = props.alternative;
  // Editable by state but not by right: say so, like the deal vehicle's discount form.
  const isMissingPermission = props.alternative.canEdit && !canEditDiscount;
  // The price is null when it is withheld; without a price the percentage ⇄ amount sync stays
  // off, so no amount or percentage is derived from it.
  const sellingPriceAmount = Number(terms.vehiclePrice?.withVat.amount ?? 0);

  const defaultValues: DiscountFormValues = {
    discountPrice: terms.discount?.withVat.amount ?? null,
    discountPercentage: terms.discount?.percentage ?? null,
    discountReason: terms.discount?.reason ?? null,
  };

  const syncAmount = (percentage: number | null) => {
    setLastTouched('percentage');
    if (sellingPriceAmount === 0) {
      return;
    }
    formApiRef.current?.clearErrors('discountPrice');
    formApiRef.current?.setValue(
      'discountPrice',
      isNil(percentage) ? null : ((sellingPriceAmount * percentage) / 100).toFixed(2)
    );
  };

  const syncPercentage = (amount: number | null) => {
    setLastTouched('amount');
    if (sellingPriceAmount === 0) {
      return;
    }
    formApiRef.current?.clearErrors('discountPercentage');
    formApiRef.current?.setValue(
      'discountPercentage',
      isNil(amount) ? null : ((amount / sellingPriceAmount) * 100).toFixed(2)
    );
  };

  const handleSubmit: FormSubmitHandler<DiscountFormValues> = (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    const percentage = values.discountPercentage?.toString() || null;
    const amount = values.discountPrice?.toString() || null;
    const reason = values.discountReason || null;
    // The field the seller typed into wins; the other one is derived by the backend.
    const discount: UpdateOfferAlternativeRequestBody['discount'] = match({
      lastTouched,
      percentage,
      amount,
    })
      .with({lastTouched: 'percentage', percentage: Pattern.string}, ({percentage}) => ({
        percentage,
        reason,
      }))
      .with({lastTouched: Pattern.not('percentage'), amount: Pattern.string}, ({amount}) => ({
        withVat: amount,
        reason,
      }))
      .otherwise(() => null);

    return patchAlternative({
      businessCaseId: props.businessCaseId,
      alternativeId: props.alternative.id,
      updateOfferAlternativeRequestBody: {
        expectedRevision: props.alternative.revision,
        discount,
      },
    })
      .unwrap()
      .then(() => reset(values))
      .catch((error: unknown) =>
        handleOfferAlternativeError(error, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <Form<DiscountFormValues>
      // Remount only when this form's own server values change, not on a sibling save.
      key={JSON.stringify(defaultValues)}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      schema={schema(terms.vehiclePrice?.withVat.amount)}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi) => {
        formApiRef.current = formApi;

        return (
          <Card
            variant="inlineGrey"
            title={i18n.t('entity.businessCase.labels.customerDiscount')}
            data-testid="offerAlternativeDiscount"
          >
            <Stack direction={['column', 'row']} spacing={4}>
              <Box flexGrow={1}>
                <NoPermissionTooltip shouldShowTooltip={isMissingPermission}>
                  <FormField
                    type="number"
                    name="discountPercentage"
                    control={control}
                    label={i18n.t('page.businessCase.labels.percentage')}
                    suffix="%"
                    isDisabled={!canEditDiscount}
                    onChange={syncAmount}
                  />
                </NoPermissionTooltip>
              </Box>
              <Box flexGrow={1}>
                <NoPermissionTooltip shouldShowTooltip={isMissingPermission}>
                  <FormField
                    type="number"
                    name="discountPrice"
                    control={control}
                    label={i18n.t('general.labels.amount')}
                    suffix={
                      isCurrency(terms.currency)
                        ? formatCurrencySymbol(terms.currency)
                        : terms.currency
                    }
                    isDisabled={!canEditDiscount}
                    onChange={syncPercentage}
                  />
                </NoPermissionTooltip>
              </Box>
              <Box flexGrow={1}>
                <NoPermissionTooltip shouldShowTooltip={isMissingPermission}>
                  <FormField
                    type="text"
                    name="discountReason"
                    control={control}
                    label={i18n.t('general.labels.reason')}
                    isDisabled={!canEditDiscount}
                  />
                </NoPermissionTooltip>
              </Box>
              <NoPermissionTooltip shouldShowTooltip={isMissingPermission}>
                <HStack justify="flex-end" align="flex-end">
                  <FormButton
                    type="submit"
                    control={control}
                    title={i18n.t('general.actions.save')}
                    isDisabled={!canEditDiscount || !formApi.formState.isDirty}
                    isLoading={isLoading}
                  />
                </HStack>
              </NoPermissionTooltip>
            </Stack>
            <Box paddingTop={2}>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.offerAlternative.discountHint', {
                  defaultValue:
                    'The amount is incl. VAT and applies to {label} only; the vehicle price stays unchanged.',
                  label: getAlternativeLabel(props.alternative),
                })}
              </Text>
            </Box>
          </Card>
        );
      }}
    </Form>
  );
}

const schema = (sellingPrice: string | undefined): SchemaOf<DiscountFormValues> =>
  object().shape({
    discountPrice: $MoneyAmount({max: {amount: sellingPrice}})
      .defined()
      .nullable(),
    discountPercentage: mixed()
      .defined()
      .test(
        'max',
        i18n.t('general.notifications.numberMaxValidationMessage'),
        (value) => !value || value <= 100
      )
      .test(
        'positive',
        i18n.t('general.notifications.numberPositive'),
        (value) => !value || value >= 0
      )
      .nullable(),
    // The reason is stored with the discount, so a reason alone would be dropped on save.
    discountReason: string()
      .defined()
      .nullable()
      .test(
        'amountForReason',
        i18n.t('entity.offerAlternative.discount.amountRequiredForReason', {
          defaultValue: 'Enter an amount or a percentage for the reason.',
        }),
        (reason, context) =>
          isNilOrEmpty(reason) ||
          isNotNilOrEmpty(context.parent.discountPrice) ||
          isNotNilOrEmpty(context.parent.discountPercentage)
      ),
  });
